import { SelectOptionType } from '@common/enums/global.enums'
import { SelectOption } from '@common/types/global.types'
import { RED_COLORS } from '@config/theme/colors'
import { Divider, MenuItem, Select as MuiSelect, Stack, styled, Typography } from '@mui/material'
import { FieldValues, useController, UseControllerProps } from 'react-hook-form'
import { FormattedMessage } from 'react-intl'

interface SelectInputProps<T extends FieldValues> extends UseControllerProps<T> {
	isDisabled?: boolean
	isRequired?: boolean
	label?: string
	options: SelectOption[]
	onChange?: (value: string) => void
	placeholder?: string
}

/**
 * Select input
 * @param control
 * @param name
 * @param label
 * @param placeholder
 * @param options
 * @param onChange
 * @param rules
 * @param isDisabled
 * @param isRequired
 * @constructor
 */
const SelectInput = <T extends FieldValues>(
	{
		control,
		name,
		label,
		placeholder,
		options,
		onChange,
		rules,
		isDisabled,
		isRequired
	}: SelectInputProps<T>
) => {
	const { field, fieldState: { error } } = useController({
		control,
		name,
		rules: {
			...rules,
			required: isRequired
		}
	})

	return (
		<Container>
			{label && (
				<Typography>
					{label}
				</Typography>
			)}
			<Select
				value={field.value}
				onChange={({ target: { value } }) => {
					if (onChange) {
						onChange(value as string)
					}
					field.onChange(value)
				}}
				renderValue={(selected) => (
					!selected
						? placeholder
						: (
							options
								.filter(option => option.type === SelectOptionType.OPTION)
								.find(option => option.value === selected)?.label
						)
				)}
				disabled={isDisabled}
				displayEmpty
			>
				{options.map((option) => (
					option.type === SelectOptionType.DIVIDER
						? <Divider key={option.key} style={{ height: '1px', width: '100%' }} />
						: (
							<MenuItem key={option.value} value={option.value}>
								{option.label}
							</MenuItem>
						)
				))}
			</Select>
			{!!error && (
				<Typography color={RED_COLORS[200]} variant="body2">
					<FormattedMessage id={`global.errors.${error.type}`} />
				</Typography>
			)}
		</Container>
	)
}

const Container = styled(Stack)({
	rowGap: '4px'
})

const Select = styled(MuiSelect)({
	height: '40px',
	'& .MuiSelect-select': {
		padding: '8px'
	}
})

export default SelectInput
